/**
 * Document generation — the formal documents produced from a submission.
 *
 * All endpoints are POST and return a binary document — a Word .docx
 * today (confirmed against dev jun-23: content-type
 * wordprocessingml.document), so the caller derives the file extension
 * from the response MIME type. Henry/Lucas:
 *   • Quote  → POST /v2/submissions/{submissionId}/quote/document
 *   • Binder → POST /v2/policy/{policyId}/binder   (needs a policy)
 *
 * The Quote may 4xx until the submission has a `final_premium` (the same
 * gate the Policy section hits); the caller surfaces the backend message.
 *
 * ─────────────────────────────────────────────────────────────────────
 * OR-1424 — Policy Form & TRIA (Andressa's Figma: two new rows in
 * Generated Files).
 *
 * The backend render engine landed in orion-backend PR #326 (Lucas,
 * aug-04: anchor-based fill, combined template with all 76 endorsements,
 * `tria`/`tria_disclosure` fill types). The two routes below are
 * confirmed with Lucas/Samuel: Policy is policy-scoped (mirroring the
 * Binder — a policy must exist first), TRIA stays submission-scoped.
 */
import { fetchDocumentReal, fetchNamedDocumentReal, uploadMultipartReal } from "../client";
import type { SubmissionWire } from "../../types/submission";
import type { JobGet, JobStatus } from "./jobs";

/** Generate the Quote document for a submission. */
export function generateQuoteDocument(submissionId: string): Promise<Blob> {
    return fetchDocumentReal(`/v2/submissions/${encodeURIComponent(submissionId)}/quote/document`, "POST");
}

/** Generate the Binder document for a policy (not a submission — a policy
 *  must already exist, created in Policy Details). */
export function generateBinderDocument(policyId: string): Promise<Blob> {
    return fetchDocumentReal(`/v2/policy/${encodeURIComponent(policyId)}/binder`, "POST");
}

/** Generate the Policy Form document for a policy (not a submission — a
 *  policy must already exist, created in Policy Details). */
export function generatePolicyDocument(policyId: string): Promise<Blob> {
    return fetchDocumentReal(`/v2/policy/${encodeURIComponent(policyId)}/policy-form`, "POST");
}

/** Generate the TRIA document for a submission. Deliberately
 *  submission-scoped and ungated on the policy: per the design brief the
 *  client can buy TRIA mid-term, so it has to be generatable on its own,
 *  not only alongside the Policy at issuance. */
export function generateTriaDocument(submissionId: string): Promise<Blob> {
    return fetchDocumentReal(`/v2/submissions/${encodeURIComponent(submissionId)}/tria/document`, "POST");
}

/** Export the submission as a pricing-model workbook (OR-1351).
 *
 *  GET (not POST — the export mutates nothing) streaming an .xlsx with
 *  the legacy template's formulas intact. The backend names the file
 *  `Pricing Model - {FNI} - {date}.xlsx` via Content-Disposition
 *  (`filename_for` in orion-backend's pricing_model/exporter.py); the
 *  caller falls back to building that same shape locally when the header
 *  doesn't reach the browser. */
export function exportPricingModelWorkbook(submissionId: string): Promise<{ blob: Blob; filename: string | null }> {
    return fetchNamedDocumentReal(`/v2/submissions/${encodeURIComponent(submissionId)}/pricing-model/export`, "GET");
}

/** The other half of OR-1351: read a pricing-model workbook back in.
 *
 *  Two routes, because the underwriter can either start from a workbook
 *  or fold one into work already in Orion:
 *    • no submission id -> a brand-new submission
 *    • with one        -> overwrites its imported sections (locations,
 *      loss runs, relativity, quote, core fields) in a single
 *      transaction. Endorsements, subjectivities, contacts and files are
 *      left alone.
 *
 *  **Both routes are asynchronous.** They answer 202 with a `job_id`;
 *  poll `waitForJob` and read `result.submission_id` when it completes.
 *  They used to return the finished `SubmissionGet` inline, which is why
 *  callers must not treat this response as a submission.
 *
 *  A workbook the backend can't read now fails the JOB rather than the
 *  request, so the per-cell problems arrive in `job.error` instead of a
 *  422 body. `pricingModelIssues` parses either, since the string is
 *  formatted the same way. A malformed or oversized upload is still
 *  rejected outright with a 422. */
export function importPricingModelWorkbook(
    file: File,
    submissionId?: string,
): Promise<PricingModelImportResponse> {
    const formData = new FormData();
    formData.append("file", file);
    const path = submissionId
        ? `/v2/submissions/${encodeURIComponent(submissionId)}/pricing-model/import`
        : "/v2/submissions/pricing-model/import";
    return uploadMultipartReal<PricingModelImportResponse>(path, formData);
}

/** 202 body from either import route. */
export interface PricingModelImportResponse {
    job_id: string;
    status: JobStatus;
}

/** The `result` payload a completed `pricing_model_import` job carries. */
export function importedSubmissionId(job: JobGet): string | null {
    const id = job.result?.submission_id;
    return typeof id === "string" && id !== "" ? id : null;
}

/** Split a pricing-model 422 into one entry per bad cell.
 *
 *  The backend formats them as
 *  `Pricing model could not be imported: Schedule!C2: msg; Premium!C4: msg`
 *  (`PricingModelError` in orion-backend). Anything that doesn't match
 *  that shape (a viability failure, an oversized upload) comes back as a
 *  single entry with no cell, so callers render one list either way. */
export function pricingModelIssues(detail: string | undefined): Array<{ sheet?: string; cell?: string; message: string }> {
    if (!detail) return [{ message: "The workbook could not be imported." }];
    const body = detail.replace(/^.*?could not be imported:\s*/i, "");
    if (body === detail) return [{ message: detail }];
    return body
        .split(";")
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => {
            const match = /^([^!]+)!([^:]+):\s*(.*)$/.exec(part);
            return match ? { sheet: match[1], cell: match[2], message: match[3] } : { message: part };
        });
}
